<script lang="ts">
  import { onMount } from 'svelte';
  import { t } from '$core/i18n';
  import { identity } from '$core/identity';
  import {
    fetchLeaderboard,
    getMyLeaderboardEntry,
    clearLeaderboardAvatar
  } from '$features/leaderboard';
  import type { LeaderboardRow, LeaderboardMetric, LeaderboardScopeKind } from '$features/leaderboard/types';
  import { socialStore } from '$features/social';
  import { Button } from '$ui/primitives/Button';
  import { Segmented } from '$ui/primitives/Segmented';
  import { EmptyState } from '$ui/primitives/EmptyState';
  import { ErrorBanner } from '$ui/primitives/ErrorBanner';
  import { SkeletonTable } from '$ui/primitives/Skeleton';

  type CountryFilter = 'off' | 'on';

  const MIN_RETENTION_SAMPLE = 30;

  let metric = $state<LeaderboardMetric>('total_minutes');
  let scopeValue = $state<string>('global');
  let countryFilter = $state<CountryFilter>('off');

  const scopeKind = $derived(
    (scopeValue.startsWith('group:') ? 'group' : scopeValue) as LeaderboardScopeKind
  );

  let entries = $state<LeaderboardRow[]>([]);
  let total = $state(0);
  let loading = $state(false);
  let errorMsg = $state<string | null>(null);
  let myCountry = $state<string | null>(null);

  let hasMore = $derived(entries.length < total);
  let showCountryTab = $derived(myCountry !== null);
  let groups = $derived(socialStore.groups);

  let skipNextAutoLoad = true;
  $effect(() => {
    void scopeValue;
    void metric;
    if (skipNextAutoLoad) {
      skipNextAutoLoad = false;
      return;
    }
    entries = [];
    total = 0;
    void load(true);
  });

  async function load(reset: boolean): Promise<void> {
    if (loading) return;
    loading = true;
    errorMsg = null;
    try {
      const page = await fetchLeaderboard({
        metric,
        scopeKind,
        country: countryFilter === 'on' ? myCountry : null,
        limit: 50,
        offset: reset ? 0 : entries.length
      });
      if (!page) {
        errorMsg = t('leaderboard.panel.errorLoad');
        return;
      }
      entries = reset ? page.entries : [...entries, ...page.entries];
      total = page.total;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void (async () => {
      const mine = await getMyLeaderboardEntry();
      if (mine && mine.country !== 'pirate') myCountry = mine.country;
      void socialStore.refreshAll();
      await load(true);
    })();
  });



  function toggleCountry(): void {
    countryFilter = countryFilter === 'on' ? 'off' : 'on';
    entries = [];
    total = 0;
    void load(true);
  }




  function formatMinutes(m: number): string {
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }

  function formatRetention(count: number, success: number): string {
    if (count < MIN_RETENTION_SAMPLE) return '—';
    return `${Math.round((success / count) * 100)}%`;
  }

  function shortCountry(code: string): string {
    if (code === 'pirate') return t('leaderboard.countryPirate');
    return code.toLowerCase();
  }

  function isMe(row: LeaderboardRow): boolean {
    return row.userId === identity.userId;
  }

  function initialOf(name: string): string {
    const trimmed = name.trim();
    if (trimmed.length === 0) return '?';
    return trimmed[0]!.toUpperCase();
  }

  function isOnline(row: LeaderboardRow): boolean {
    if (!(row as any).last_seen_at) return false;
    const last = new Date((row as any).last_seen_at).getTime();
    if (Number.isNaN(last)) return false;
    return Date.now() - last < 15 * 60 * 1000;
  }

  function onAvatarError(e: Event): void {
    const img = e.currentTarget as HTMLImageElement;
    img.remove();
    // Si el fallo fue en la fila propia, limpia el avatar_url remoto
    // para no seguir haciendo 400 en cada carga.
    const row = entries.find((r) => r.avatarUrl === img.src);
    if (row && isMe(row)) {
      void clearLeaderboardAvatar();
    }
  }
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('leaderboard.panel.title')}</h1>
  </header>

  <div class="controls">
    <Segmented
      bind:value={metric}
      options={[
        { value: 'total_minutes', label: t('leaderboard.panel.metricMinutes') },
        { value: 'problems_today', label: t('leaderboard.panel.metricProblems') },
        { value: 'current_streak', label: t('leaderboard.panel.metricStreak') },
        { value: 'total_problems', label: t('leaderboard.panel.metricTotalProblems') },
        { value: 'retention', label: t('leaderboard.panel.metricRetention') }
      ]}
      ariaLabel={t('leaderboard.panel.title')}
    />
  </div>

  <div class="controls">
    <Segmented
      bind:value={scopeValue}
      options={[
        { value: 'global', label: t('leaderboard.panel.tabGlobal') },
        { value: 'friends', label: t('leaderboard.panel.tabFriends') },
        ...groups.map((g) => ({ value: 'group:' + g.id, label: g.name }))
      ]}
      ariaLabel={t('leaderboard.panel.title')}
    />

    {#if showCountryTab}
      <Button
        variant={countryFilter === 'on' ? 'filled' : 'outline'}
        size="sm"
        onclick={toggleCountry}
      >
        {t('leaderboard.panel.tabCountry', { code: shortCountry(myCountry ?? '') })}
      </Button>
    {/if}
  </div>

  {#if errorMsg}
    <ErrorBanner
      variant="error"
      message={errorMsg}
      action={{ label: t('common.retry'), onclick: () => void load(true) }}
    />
  {/if}

  {#if loading && entries.length === 0}
    <SkeletonTable rows={8} columns={6} />
  {:else if entries.length === 0}
    {#if (scopeKind as string) === 'friends'}
      <EmptyState
        icon="users"
        title={t('leaderboard.panel.friendsEmpty')}
        description={t('leaderboard.panel.friendsEmptyHint')}
      />
    {:else if (scopeKind as string) === 'group'}
      <EmptyState
        icon="users-round"
        title={t('leaderboard.panel.groupEmpty')}
        description={t('leaderboard.panel.groupEmptyHint')}
      />
    {:else}
      <EmptyState
        icon="trophy"
        title={t('leaderboard.panel.empty')}
        description={t('leaderboard.panel.emptyHint')}
      />
    {/if}
  {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="rank">{t('leaderboard.panel.colRank')}</th>
              <th>{t('leaderboard.panel.colUser')}</th>
              <th class="country">{t('leaderboard.panel.colCountry')}</th>
              <th class="num">{t('leaderboard.panel.colProblemsToday')}</th>
              <th class="num">{t('leaderboard.panel.colMinutesToday')}</th>
              <th class="num">{t('leaderboard.panel.colStreak')}</th>
              <th class="num">{t('leaderboard.panel.colTotal')}</th>
              <th class="num">{t('leaderboard.panel.colRetention')}</th>
            </tr>
          </thead>
          <tbody>
            {#each entries as row, i (row.userId)}
              <tr class:me={isMe(row)}>
                <td class="rank">{i + 1}</td>
                <td class="user-cell">
                  <div class="user-cell-inner">
                    <span
                      class="status-dot"
                      class:online={isOnline(row)}
                      aria-hidden="true"
                    ></span>
                    <span class="avatar-wrap">
                      <span class="avatar-initial">{initialOf(row.displayName)}</span>
                      {#if row.avatarUrl}
                        <img
                          class="avatar-img"
                          src={row.avatarUrl}
                          alt=""
                          loading="lazy"
                          onerror={onAvatarError}
                        />
                      {/if}
                    </span>
                    <span class="user-name">{row.displayName}</span>
                    {#if isMe(row)}
                      <span class="me-tag">{t('leaderboard.panel.youLabel')}</span>
                    {/if}
                  </div>
                </td>
                <td class="country">{shortCountry(row.country)}</td>
                <td class="num">{(row as any).problems_today ?? '—'}</td>
                <td class="num">{formatMinutes((row as any).minutes_today ?? 0)}</td>
                <td class="num">{(row as any).current_streak ?? 0}</td>
                <td class="num">{formatMinutes(row.lifetimeMinutes ?? 0)}</td>
                <td class="num">
                  {formatRetention((row as any).mature_reviews_count ?? 0, (row as any).mature_success_count ?? 0)}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      {#if hasMore}
        <div class="load-more">
          <button
            class="primary"
            disabled={loading}
            onclick={() => void load(false)}
          >
            {loading ? t('leaderboard.panel.loading') : t('leaderboard.panel.loadMore')}
          </button>
        </div>
      {/if}
    {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    align-items: center;
    justify-content: space-between;
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-body);
  }

  thead {
    position: sticky;
    top: 0;
    background: var(--color-surface-sunken);
    z-index: 1;
  }

  th {
    padding: var(--space-3) var(--space-4);
    text-align: left;
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: var(--text-label-track);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  th.rank,
  th.country {
    width: 5rem;
  }

  th.num {
    text-align: right;
  }

  tbody tr {
    border-bottom: var(--border-width) solid var(--color-border);
  }

  tbody tr.me {
    background: var(--color-accent-tint);
    border-left: 3px solid var(--color-accent);
  }

  td {
    padding: var(--space-3) var(--space-4);
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
  }

  td.rank {
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }

  td.country {
    color: var(--color-text-muted);
    font-family: var(--font-mono);
    text-transform: uppercase;
    font-size: var(--text-caption);
  }

  td.num {
    text-align: right;
    font-family: var(--font-mono);
  }

  .user-cell {
    min-width: 0;
  }

  .user-cell-inner {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
  }

  .user-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-offline);
    flex: 0 0 auto;
  }

  .status-dot.online {
    background: var(--color-online);
    box-shadow: 0 0 0 2px var(--color-success-tint);
  }

  .avatar-wrap {
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    flex: 0 0 auto;
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .avatar-initial {
    font-size: 11px;
    font-family: var(--font-mono);
    color: var(--color-text-muted);
    text-transform: uppercase;
    line-height: 1;
  }

  .avatar-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .me-tag {
    margin-left: var(--space-2);
    padding: 0 var(--space-1);
    font-size: 10px;
    color: var(--color-accent);
    border: var(--border-width) solid var(--color-accent);
    border-radius: var(--radius-xs);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: var(--weight-medium);
  }

  .load-more {
    display: flex;
    justify-content: center;
    padding: var(--space-4);
  }
</style>